import { observer } from "mobx-react";
import React, { useState } from "react";
import {
  Stack,
  Button,
  Text,
  Group,
  Alert,
  Center,
  AspectRatio,
  ActionIcon,
} from "@mantine/core";
import { IconAlertTriangle, IconCheck, IconCopy } from "@tabler/icons-react";
import { QRCodeSVG } from "qrcode.react";
import { Cubby } from "../../stores/Cubby";
import { CopyButton } from "@mantine/core";
import ConnectionStatusBadge from "../interactiveData/connectionStatusBadge";

interface ShowNostrConnectionProps {
  cubby: Cubby;
}

const ShowNostrConnection = observer(({ cubby }: ShowNostrConnectionProps) => {
  const connection = cubby.selectedNostrConnection;
  const [showQRCode, setShowQRCode] = useState(false);

  if (!connection) {
    return null;
  }

  return (
    <Stack gap="md">
      <Center>
        <Text>
          <strong>{connection.name}</strong>
        </Text>
      </Center>
      <Center>
        <ConnectionStatusBadge connection={connection} />
      </Center>
      {connection.security_level_name === "send_and_receive" && !showQRCode ? (
        <>
          <Alert
            icon={<IconAlertTriangle />}
            title="Warning"
            color="red"
            radius="md"
          >
            Don’t reveal this QR or connection code to anyone, except for apps
            and websites that you trust. Anyone who has it could spend funds
            from your node!
          </Alert>

          <Button
            variant="outline"
            color="blue"
            onClick={() => setShowQRCode(true)}
          >
            OK, show me the code
          </Button>
        </>
      ) : (
        <>
          <Center>
            <AspectRatio ratio={1} style={{ maxWidth: 300 }}>
              <QRCodeSVG
                value={connection.nwc_uri}
                size={300}
                bgColor="#ffffff"
                marginSize={6}
                fgColor="#000000"
                level="L"
                title="NWC Connection Code"
              />
            </AspectRatio>
          </Center>
          <Center>
            <Group gap="xs" align="center">
              <CopyButton value={connection.nwc_uri}>
                {({ copied, copy }) => (
                  <Button
                    color={copied ? "teal" : "blue"}
                    onClick={copy}
                    leftSection={
                      copied ? <IconCheck size={16} /> : <IconCopy size={16} />
                    }
                  >
                    {copied ? "Copied!" : "Copy Connection Code"}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </Center>
          {connection.security_level_name === "send_and_receive" && (
            <Alert color="red">
              Anyone who sees this QR code or connection code can spend funds
              from your node! Only share it with apps you trust.
            </Alert>
          )}

          {connection.security_level_name === "receive_only" && (
            <Alert>
              This connection is safe to share with any app. It can only
              receive.
            </Alert>
          )}
        </>
      )}
    </Stack>
  );
});

export default ShowNostrConnection;
